import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
  DraftStatus,
  InkFormData,
  InspectionDto,
  InspectionFieldDto,
  InspectionModel,
  RecognizeBatchResponse,
  RecognizeDraftRequest,
  RecognizeDraftResponse,
  RecognizePrepareResponse
} from '@authority-labs/api-interfaces';
import { CrudService } from '@check-list/app/shared/services/crud/crud.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ProfessionalizeResponse {
  original: string;
  result: string;
}

@Injectable({
  providedIn: 'root'
})
export class InspectionsService extends CrudService<InspectionDto> {
  constructor(injector: Injector) {
    super(injector, 'inspections', InspectionModel);
    this.http = injector.get(HttpClient);
  }

  createOne(
    item: InspectionDto,
    queryParams: string = ''
  ): Observable<InspectionDto> {
    const inspection = new InspectionModel(item);
    return super.createOne(inspection, queryParams);
  }

  plainToClass = (item: InspectionDto): InspectionModel =>
    new InspectionModel(item);

  // Draft methods
  getDraftData(inspectionId: number): Observable<{
    inkFormData: InkFormData;
    inspectionData: InspectionDto;
    status: DraftStatus;
  }> {
    return this.http.get<{
      inkFormData: InkFormData;
      inspectionData: InspectionDto;
      status: DraftStatus;
    }>(`${this.api}/${inspectionId}/draft`);
  }

  saveDraftData(
    inspectionId: number,
    inkFormData: InkFormData
  ): Observable<InkFormData> {
    return this.http.put<InkFormData>(`${this.api}/${inspectionId}/draft`, {
      inkFormData
    });
  }

  patchDraftData(
    inspectionId: number,
    inkFormData: InkFormData
  ): Observable<InkFormData> {
    return this.http.patch<InkFormData>(`${this.api}/${inspectionId}/draft`, {
      inkFormData
    });
  }

  saveDraftSnapshot(
    inspectionId: number,
    inspectionData: InspectionDto
  ): Observable<{ inspectionData: InspectionDto }> {
    return this.http.put<{ inspectionData: InspectionDto }>(
      `${this.api}/${inspectionId}/draft/snapshot`,
      { inspectionData }
    );
  }

  updateDraftStatus(
    inspectionId: number,
    status: DraftStatus
  ): Observable<{ status: DraftStatus }> {
    return this.http.patch<{ status: DraftStatus }>(
      `${this.api}/${inspectionId}/draft/status`,
      { status }
    );
  }

  recognizeDraft(
    inspectionId: number,
    request: RecognizeDraftRequest = {}
  ): Observable<RecognizeDraftResponse> {
    return this.http.post<RecognizeDraftResponse>(
      `${this.api}/${inspectionId}/draft/recognize`,
      request
    );
  }

  recognizePrepare(
    inspectionId: number,
    request: RecognizeDraftRequest = {}
  ): Observable<RecognizePrepareResponse> {
    return this.http.post<RecognizePrepareResponse>(
      `${this.api}/${inspectionId}/draft/recognize-prepare`,
      request
    );
  }

  recognizeBatch(
    inspectionId: number,
    fieldIds: string[]
  ): Observable<RecognizeBatchResponse> {
    return this.http.post<RecognizeBatchResponse>(
      `${this.api}/${inspectionId}/draft/recognize-batch`,
      { fieldIds }
    );
  }

  professionalize(dto: InspectionFieldDto): Observable<string> {
    return this.http
      .post<ProfessionalizeResponse>(`${this.api}/professionalize`, dto)
      .pipe(map((response) => response.result));
  }
}
